import { useCallback, useRef, useState } from "react"
import { css } from "@emotion/react"
import EpisodeItem from "./EpisodeItem"
import type { Episode } from "../types"

export default function EpisodeList({
  episodes,
  selectedEpisode,
  onSelectEpisode,
  isPlaying,
}: {
  episodes: Episode[]
  selectedEpisode: Episode
  onSelectEpisode: (episode: Episode) => void
  isPlaying: boolean
}) {
  const [activeIndex, setActiveIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const optionRefs = useRef<(HTMLDivElement | null)[]>([])

  const focusEpisode = useCallback((index: number) => {
    setActiveIndex(index)
    optionRefs.current[index]?.focus({
      // @ts-expect-error `focusVisible` not yet recognized by TS, but supported in Safari
      focusVisible: true,
    })
  }, [])

  const handleContainerKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // Only handle keys when the container itself is focused, not its children
      if (event.target !== containerRef.current) return

      if (event.key === "ArrowDown") {
        event.preventDefault()
        focusEpisode(0)
      } else if (event.key === "ArrowUp") {
        event.preventDefault()
        focusEpisode(episodes.length - 1)
      }
    },
    [episodes.length, focusEpisode],
  )

  const handleEpisodeKeyDown = useCallback(
    (event: React.KeyboardEvent, index: number) => {
      let nextIndex: number | null = null

      switch (event.key) {
        case "ArrowDown":
          nextIndex = Math.min(index + 1, episodes.length - 1)
          break
        case "ArrowUp":
          nextIndex = Math.max(index - 1, 0)
          break
        case "Home":
          nextIndex = 0
          break
        case "End":
          nextIndex = episodes.length - 1
          break
        default:
          return
      }

      event.preventDefault()
      focusEpisode(nextIndex)
    },
    [episodes.length, focusEpisode],
  )

  const handleEpisodeClick = useCallback(
    (episode: Episode, index: number) => {
      setActiveIndex(index)
      onSelectEpisode(episode)
    },
    [onSelectEpisode],
  )

  return (
    <>
      <div aria-live="polite" tabIndex={-1} className="visually-hidden">
        {`Selected: ${selectedEpisode.title}`}
      </div>
      <div
        ref={containerRef}
        role="listbox"
        aria-label="Podcast episodes"
        tabIndex={0}
        onKeyDown={handleContainerKeyDown}
        css={styles.container}
      >
        {episodes.map((episode, index) => {
          const selected = selectedEpisode.audioUrl === episode.audioUrl
          return (
            <EpisodeItem
              key={episode.audioUrl}
              episode={episode}
              onSelect={handleEpisodeClick}
              isSelected={selected}
              isPlaying={selected && isPlaying}
              index={index}
              tabIndex={index === activeIndex ? 0 : -1}
              onKeyDown={handleEpisodeKeyDown}
              optionRef={(el) => {
                optionRefs.current[index] = el
              }}
            />
          )
        })}
      </div>
    </>
  )
}

const styles = {
  container: css`
    &:focus-visible {
      outline: 2px solid var(--color-topaz);
      outline-offset: -2px;
      border-radius: 4px;
    }
  `,
}
